import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { LanguageService } from './language.service';
import { environment } from '../../environments/environment';
import { 
  JournalDTO, 
  BookDTO, 
  ArticleDTO, 
  ChapterDTO, 
  PersonDTO, 
  SeriDTO, 
  ContactDTO,
  SubscribeDTO 
} from '../models/series.model';
import { AdvancedSearchRequestDTO } from '../models/search.model';

export interface JournalGroup {
  name: string;
  list: JournalDTO[];
}

export interface BookGroup {
  name: string;
  list: BookDTO[];
}

export interface Summary {
  journals: number;
  books: number;
  articles: number;
  authors: number;
}

export interface SlideItem {
  IMAGE: string;
  LINK?: string;
  ALT?: string;
}

export interface SliderItem {
  ID: number;
  LINK: string;
  RANK: number | null;
  IMAGE: string;
  ALT: string | null;
  ISACTIVE: number;
  CREATEDDATE: string | null;
  CREATEDUSER: number;
  ISDELETED: number;
  MODIFIEDDATE: string | null;
  MODIFIEDUSER: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private currentLangSubject = new BehaviorSubject<string>('tr');
  public currentLang$ = this.currentLangSubject.asObservable();
  private apiUrl: string = environment.apiUrl + 'api/webclient/';

  constructor(
    private http: HttpClient,
    private languageService: LanguageService
  ) {}

  setCurrentLang(lang: string): void {
    this.currentLangSubject.next(lang);
    // LanguageService ile senkronize et
    if (this.languageService.getCurrentLanguage() !== lang) {
      this.languageService.setLanguage(lang);
    }
  }

  getCurrentLang(): string {
    // LanguageService'ten mevcut dili al
    return this.languageService.getCurrentLanguage();
  }

  getData(endpoint: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/${endpoint}`);
  }

  postData(endpoint: string, data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/${endpoint}`, data);
  }

  putData(endpoint: string, data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/${endpoint}`, data);
  }

  deleteData(endpoint: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${endpoint}`);
  }

  // DergiPark specific methods
  getAllJournals(): Observable<JournalGroup[]> {
    return this.http.get<any>(`${this.apiUrl}getAllJournals?lang=${this.getCurrentLang()}`)
      .pipe(
        map((response: any) => {
          const journalGroups = response.Data || [];
          
          const result = journalGroups.map((group: any) => {
            const name = group.name.startsWith('@') ? group.name.substring(1) : group.name;
            
            const list = group.list.map((journal: any) => {
              if (!journal.coverShowcaseImageUrl && journal.cover) {
                journal.coverShowcaseImageUrl = journal.cover;
              }
              return journal;
            });
            
            return {
              name: name,
              list: list
            };
          });
          
          return result;
        })
      );
  }

  getAllBooks(): Observable<BookGroup[]> {
    return this.http.get<any>(`${this.apiUrl}getAllBooks?lang=${this.getCurrentLang()}`)
      .pipe(
        map((response: any) => {
          const bookGroups = response.Data || [];
          
          const result = bookGroups.map((group: any) => {
            const name = group.name.startsWith('@') ? group.name.substring(1) : group.name;
            
            const list = group.list.map((book: any) => {
              if (!book.coverShowcaseImageUrl && book.cover) {
                book.coverShowcaseImageUrl = book.cover;
              }
              return book;
            });
            
            return {
              name: name,
              list: list
            };
          });
          
          return result;
        })
      );
  }

  getSummary(): Observable<Summary> {
    return this.http.get<any>(`${this.apiUrl}getSummary`)
      .pipe(
        map((response: any) => {
          const summaryData = response.Data || {};
          
          const summary: Summary = {
            journals: summaryData.journals || 0,
            books: summaryData.books || 0,
            articles: summaryData.articles || 0,
            authors: summaryData.authors || 0
          };
          
          return summary;
        })
      );
  }

  getSliderItemsList(): Observable<SliderItem[]> {
    return this.http.get<any>(`${this.apiUrl}getSliders`)
      .pipe(
        map((response: any) => {
          const sliderItems = response.Data || [];
          
          return sliderItems
            .filter((item: SliderItem) => item.ISACTIVE === 1)
            .sort((a: SliderItem, b: SliderItem) => {
              if (a.RANK === null) return 1;
              if (b.RANK === null) return -1;
              return a.RANK - b.RANK;
            });
        })
      );
  }

  // Individual item methods (Observable-based)
  getArticleBySlug(slug: string, id?: number): Observable<ArticleDTO | null> {
    // If ID is provided, use getarticle endpoint (requires both slug and ID)
    // If ID is not provided, use getarticle2 endpoint (requires only slug)
    let url: string;
    
    if (id) {
      url = `${this.apiUrl}getarticle?lang=${this.getCurrentLang()}&slug=${slug}&ID=${id}`;
      console.log(`Using getarticle endpoint with ID: ${id}`);
    } else {
      url = `${this.apiUrl}getarticle2?lang=${this.getCurrentLang()}&slug=${slug}`;
      console.log(`Using getarticle2 endpoint without ID`);
    }
    
    return this.http.get<any>(url)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { 
          console.error('getArticleBySlug error:', error); 
          return of(null); 
        })
      );
  }

  getJournalBySlug(slug: string): Observable<JournalDTO | null> {
    return this.http.get<any>(`${this.apiUrl}getjournal?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournalBySlug error:', error); return of(null); })
      );
  }

  getJournalLiteBySlug(slug: string): Observable<JournalDTO | null> {
    return this.http.get<any>(`${this.apiUrl}getjournalInfo?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournalLiteBySlug error:', error); return of(null); })
      );
  }

  getJournalContentsBySlug(slug: string): Observable<any | null> {
    return this.http.get<any>(`${this.apiUrl}getJournalContents?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournalContentsBySlug error:', error); return of(null); })
      );
  }

  getIssueBySlug(journalSlug: string, issueSlug: string): Observable<any | null> {
    return this.http.get<any>(`${this.apiUrl}getIssue?lang=${this.getCurrentLang()}&journal=${journalSlug}&issue=${issueSlug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getIssueBySlug error:', error); return of(null); })
      );
  }

  getIssuesByJournalSlug(slug: string): Observable<any[] | null> {
    return this.http.get<any>(`${this.apiUrl}getIssues?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getIssuesByJournalSlug error:', error); return of(null); })
      );
  }

  getAnnouncementsByJournalSlug(slug: string): Observable<any[] | null> {
    return this.http.get<any>(`${this.apiUrl}getJournalAnnouncements?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getAnnouncementsByJournalSlug error:', error); return of(null); })
      );
  }

  getBookMetaBySlug(slug: string): Observable<BookDTO | null> {
    return this.http.get<any>(`${this.apiUrl}getBookInfo?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getBookMetaBySlug error:', error); return of(null); })
      );
  }

  getBookBySlug(slug: string): Observable<BookDTO | null> {
    return this.http.get<any>(`${this.apiUrl}getBook?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getBookBySlug error:', error); return of(null); })
      );
  }

  getChapterBySlug(slug: string): Observable<ChapterDTO | null> {
    return this.http.get<any>(`${this.apiUrl}getChapter?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getChapterBySlug error:', error); return of(null); })
      );
  }

  getSeriBySlug(slug: string): Observable<SeriDTO | null> {
    const url = `${this.apiUrl}getSeriBySlugAsync?lang=${this.getCurrentLang()}&slug=${slug}`;
    
    return this.http.get<any>(url)
      .pipe(
        timeout(30000), // 30 second timeout
        map(response => response?.Data || null),
        catchError(error => { 
          console.error('getSeriBySlug error:', error);
          return of(null); 
        })
      );
  }

  getBooksSeriBySeri(slug: string): Observable<BookDTO[] | null> {
    const url = `${this.apiUrl}getBooksSeriBySeriAsync?lang=${this.getCurrentLang()}&seriSlug=${slug}`;
    
    return this.http.get<any>(url, {
      responseType: 'json',
      observe: 'body'
    })
      .pipe(
        timeout(60000), // 60 seconds timeout for large response
        map(response => {
          if (response && response.Data) {
            return response.Data;
          }
          return [];
        }),
        catchError(error => { 
          console.error('getBooksSeriBySeri error:', error);
          return of([]);
        })
      );
  }

  getPersonDetail(slug: string): Observable<PersonDTO | null> {
    return this.http.get<any>(`${this.apiUrl}getPersonDetail?lang=${this.getCurrentLang()}&value=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getPersonDetail error:', error); return of(null); })
      );
  }

  // Journal methods
  getJournal(slug: string): Observable<any | null> {
    return this.http.get<any>(`${this.apiUrl}getJournal?lang=${this.getCurrentLang()}&slug=${slug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournal error:', error); return of(null); })
      );
  }

  getJournalIssues(slug: string, page: number = 1, pageSize: number = 20): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}getJournalIssues?lang=${this.getCurrentLang()}&slug=${slug}&page=${page}&pageSize=${pageSize}`)
      .pipe(
        map(response => ({
          data: response?.Data || [],
          totalPages: response?.TotalPages || 1,
          totalCount: response?.TotalCount || 0
        })),
        catchError(error => { 
          console.error('getJournalIssues error:', error); 
          return of({ data: [], totalPages: 1, totalCount: 0 }); 
        })
      );
  }

  getJournalIssue(journalSlug: string, issueSlug: string): Observable<any | null> {
    return this.http.get<any>(`${this.apiUrl}getJournalIssue?lang=${this.getCurrentLang()}&journalSlug=${journalSlug}&issueSlug=${issueSlug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournalIssue error:', error); return of(null); })
      );
  }

  getIssueArticles(journalSlug: string, issueSlug: string): Observable<any[] | null> {
    return this.http.get<any>(`${this.apiUrl}getIssueArticles?lang=${this.getCurrentLang()}&journalSlug=${journalSlug}&issueSlug=${issueSlug}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { console.error('getIssueArticles error:', error); return of([]); })
      );
  }

  getJournalArticle(journalSlug: string, articleSlug: string): Observable<any | null> {
    return this.http.get<any>(`${this.apiUrl}getJournalArticle?lang=${this.getCurrentLang()}&journalSlug=${journalSlug}&articleSlug=${articleSlug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournalArticle error:', error); return of(null); })
      );
  }

  getArticleViews(journalSlug: string, articleSlug: string): Observable<number> {
    return this.http.get<any>(`${this.apiUrl}getArticleViews?journalSlug=${journalSlug}&articleSlug=${articleSlug}`)
      .pipe(
        map(response => response?.Data || 0),
        catchError(error => { console.error('getArticleViews error:', error); return of(0); })
      );
  }

  getArticleDownloads(journalSlug: string, articleSlug: string): Observable<number> {
    return this.http.get<any>(`${this.apiUrl}getArticleDownloads?journalSlug=${journalSlug}&articleSlug=${articleSlug}`)
      .pipe(
        map(response => response?.Data || 0),
        catchError(error => { console.error('getArticleDownloads error:', error); return of(0); })
      );
  }

  getRelatedArticles(journalSlug: string, articleSlug: string): Observable<any[] | null> {
    return this.http.get<any>(`${this.apiUrl}getRelatedArticles?lang=${this.getCurrentLang()}&journalSlug=${journalSlug}&articleSlug=${articleSlug}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { console.error('getRelatedArticles error:', error); return of([]); })
      );
  }

  getJournalContent(journalSlug: string, contentSlug: string): Observable<any | null> {
    return this.http.get<any>(`${this.apiUrl}getJournalContent?lang=${this.getCurrentLang()}&journalSlug=${journalSlug}&contentSlug=${contentSlug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournalContent error:', error); return of(null); })
      );
  }

  getJournalAnnouncement(journalSlug: string, announcementSlug: string): Observable<any | null> {
    return this.http.get<any>(`${this.apiUrl}getJournalAnnouncement?lang=${this.getCurrentLang()}&journalSlug=${journalSlug}&announcementSlug=${announcementSlug}`)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('getJournalAnnouncement error:', error); return of(null); })
      );
  }

  // Post methods
  subscribe(subscribeData: SubscribeDTO): Observable<any | null> {
    return this.http.post<any>(`${this.apiUrl}subscribe?lang=${this.getCurrentLang()}`, subscribeData)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('subscribe error:', error); return of(null); })
      );
  }

  subscribeBook(subscribeData: SubscribeDTO): Observable<any | null> {
    return this.http.post<any>(`${this.apiUrl}subscribeBook?lang=${this.getCurrentLang()}`, subscribeData)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('subscribeBook error:', error); return of(null); })
      );
  }

  contact(contactData: ContactDTO): Observable<any | null> {
    return this.http.post<any>(`${this.apiUrl}contact?lang=${this.getCurrentLang()}`, contactData)
      .pipe(
        map(response => response?.Data || null),
        catchError(error => { console.error('contact error:', error); return of(null); })
      );
  }

  subscribeVerify(token: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}subscribeVerify?lang=${this.getCurrentLang()}&token=${token}`, {})
      .pipe(
        map(response => response),
        catchError(error => { 
          console.error('subscribeVerify error:', error); 
          return of({ error: error });
        })
      );
  }

  // Search methods
  getFilters(type: string): Observable<any[]> {
    const url = `${this.apiUrl}getFilters?Value=${type}`;
    return this.http.get<any>(url)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { 
          console.error('getFilters error:', error); 
          return of([]); 
        })
      );
  }

  getFilteredBooks(filterObject: any): Observable<BookDTO[]> {
    const params = new URLSearchParams(filterObject).toString();
    const queryString = params ? `&${params}` : '';
    return this.http.get<any>(`${this.apiUrl}getFilteredBooks?lang=${this.getCurrentLang()}${queryString}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { console.error('getFilteredBooks error:', error); return of([]); })
      );
  }

  getFilteredJournals(filterObject: any): Observable<JournalDTO[]> {
    const params = new URLSearchParams(filterObject).toString();
    const queryString = params ? `&${params}` : '';
    return this.http.get<any>(`${this.apiUrl}getFilteredJournals?lang=${this.getCurrentLang()}${queryString}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { console.error('getFilteredJournals error:', error); return of([]); })
      );
  }

  getFilteredArticles(filterObject: any): Observable<ArticleDTO[]> {
    const params = new URLSearchParams(filterObject).toString();
    const queryString = params ? `&${params}` : '';
    return this.http.get<any>(`${this.apiUrl}getFilteredArticles?lang=${this.getCurrentLang()}${queryString}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { console.error('getFilteredArticles error:', error); return of([]); })
      );
  }

  getFilteredChapters(filterObject: any): Observable<ChapterDTO[]> {
    const params = new URLSearchParams(filterObject).toString();
    const queryString = params ? `&${params}` : '';
    return this.http.get<any>(`${this.apiUrl}getFilteredChapters?lang=${this.getCurrentLang()}${queryString}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { console.error('getFilteredChapters error:', error); return of([]); })
      );
  }

  getFilteredPerson(filterObject: any): Observable<PersonDTO[]> {
    const params = new URLSearchParams(filterObject).toString();
    const queryString = params ? `&${params}` : '';
    return this.http.get<any>(`${this.apiUrl}getFilteredPerson?lang=${this.getCurrentLang()}${queryString}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { console.error('getFilteredPerson error:', error); return of([]); })
      );
  }

  getFilteredSeries(filterObject: any): Observable<SeriDTO[]> {
    const params = new URLSearchParams(filterObject).toString();
    const queryString = params ? `&${params}` : '';
    const url = `${this.apiUrl}getFilteredSeries?lang=${this.getCurrentLang()}${queryString}`;
    return this.http.get<any>(url)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { 
          console.error('getFilteredSeries error:', error); 
          return of([]); 
        })
      );
  }

  getArticlesByKeyword(keyword: string): Observable<ArticleDTO[]> {
    return this.http.get<any>(`${this.apiUrl}getArticlesByKeyword?lang=${this.getCurrentLang()}&keyword=${encodeURIComponent(keyword)}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { 
          console.error('getArticlesByKeyword error:', error); 
          return of([]); 
        })
      );
  }

  getArticlesByClassification(classificationTitle: string): Observable<ArticleDTO[]> {
    return this.http.get<any>(`${this.apiUrl}getArticlesByClassification?lang=${this.getCurrentLang()}&keyword=${encodeURIComponent(classificationTitle)}`)
      .pipe(
        map(response => response?.Data || []),
        catchError(error => { 
          console.error('getArticlesByClassification error:', error); 
          return of([]); 
        })
      );
  }

  getPdfViaProxy(pdfUrl: string): Observable<ArrayBuffer> {
    const proxyUrl = `${this.apiUrl}PdfProxy?url=${encodeURIComponent(pdfUrl)}`;
    
    return this.http.get(proxyUrl, { responseType: 'arraybuffer' }).pipe(
      catchError(error => {
        console.error('API Service: Error fetching PDF via proxy:', error);
        throw error;
      })
    );
  }

  setCurrentLangAsync(value: string): void {
    if (value === 'iu') {
      return;
    }
    if (value) {
      this.setCurrentLang(value);
    }
  }

  // Advanced Search
  advancedSearch(request: AdvancedSearchRequestDTO): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}advancedSearch`, request)
      .pipe(
        map((response: any) => {
          return response.Data || {
            Results: [],
            Metadata: {
              TotalResults: 0,
              Page: 1,
              PageSize: 20,
              TotalPages: 0,
              SearchTime: 0,
              Query: ''
            },
            Facets: []
          };
        }),
        catchError(error => {
          console.error('API Service: Error performing advanced search:', error);
          return of({
            Results: [],
            Metadata: {
              TotalResults: 0,
              Page: 1,
              PageSize: 20,
              TotalPages: 0,
              SearchTime: 0,
              Query: ''
            },
            Facets: []
          });
        })
      );
  }
}